import { put, takeLatest, call, all } from "redux-saga/effects";
import axios from "axios";
import { API_URL } from "../../../config";
import { SESSION_EXPIRED } from "../../../_utils/constants/Auth";
import { hydrateEstimate } from "../../../_utils/manualEstimatePayload";
import {
  CC_ESTIMATE_SAVE_REQUEST,
  CC_ESTIMATE_SAVE_SUCCESS,
  CC_ESTIMATE_SAVE_FAILED,
  CC_ESTIMATE_LOAD_REQUEST,
  CC_ESTIMATE_LOAD_SUCCESS,
  CC_ESTIMATE_LOAD_FAILED,
  CC_SCOPE_PHOTO_UPLOAD_REQUEST,
  CC_SCOPE_PHOTO_UPLOAD_SUCCESS,
  CC_SCOPE_PHOTO_UPLOAD_FAILED,
  CC_SCOPES_REQUEST,
  CC_SCOPES_SUCCESS,
  CC_SCOPES_FAILED,
} from "../../../_utils/constants/CountertopCabinetEstimate";

// Persist a Countertops/Cabinets manual estimate. The payload's `estimateId` decides create vs
// update (0 = create, >0 = update) so re-saving never duplicates. Reuses the existing
// axios-in-saga + Bearer auth + 401 -> SESSION_EXPIRED convention.
export function* saveCountertopCabinetEstimate({ token, payload }) {
  try {
    const response = yield call(
      axios.post,
      `${API_URL}/Ordering/api/Order/SaveCountertopCabinetEstimate`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    yield put({ type: CC_ESTIMATE_SAVE_SUCCESS, payload: response.data });
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put({ type: SESSION_EXPIRED });
    } else {
      yield put({ type: CC_ESTIMATE_SAVE_FAILED, payload: error?.response });
    }
  }
}

// Load a saved estimate for a customer + floor plan (+ optional variant).
export function* loadCountertopCabinetEstimate({ token, contactInfoId, unitTypeId, variantId = 0, cnCMeasureQuantityId = 0 }) {
  const cnCId = Number(cnCMeasureQuantityId) || 0;
  const url = `${API_URL}/Ordering/api/Order/GetCountertopCabinetEstimate?ContactInfoId=${contactInfoId}&UnitTypeId=${unitTypeId}${cnCId > 0 ? `&CnCMeasureQuantityId=${cnCId}` : ""}`;
  try {
    const response = yield call(
      axios.get,
      url,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const source = response.data?.result ?? response.data;
    // Inspect the raw rows before quantity-based label recovery, so LF/EA
    // coverage from the catalog can take precedence over a direct quantity match.
    const structureIds = [...new Set((Array.isArray(source?.labor) ? source.labor : [])
      .filter((item) => !item.parentProductName && !Number(item.parentProductId) && !Number(item.kitId))
      .map((item) => Number(item.productStructureId)).filter((id) => id > 0))];

    // Older saved labor rows have no kitId. Recover the relationship from the
    // same API used by Add Estimate, without replacing saved prices/quantities
    // or touching the modal's shared Redux catalog lists.
    if (structureIds.length && Number(source?.storeId) > 0) {
      const catalogs = yield all(structureIds.map((productStructureId) => call(
        fetchSavedLaborKitLinks, token, source.storeId, contactInfoId, productStructureId
      )));
      const labeled = hydrateEstimate(response.data, catalogs.flat());
      const enriched = { ...source, material: labeled.materialList, labor: labeled.laborList };
      yield put({
        type: CC_ESTIMATE_LOAD_SUCCESS,
        payload: response.data?.result ? { ...response.data, result: enriched } : enriched,
      });
    } else {
      yield put({ type: CC_ESTIMATE_LOAD_SUCCESS, payload: response.data });
    }
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put({ type: SESSION_EXPIRED });
    } else {
      yield put({ type: CC_ESTIMATE_LOAD_FAILED, payload: error?.response });
    }
  }
}

function* fetchSavedLaborKitLinks(token, storeId, contactInfoId, productStructureId) {
  const params = new URLSearchParams({
    ProductStructureId: productStructureId,
    ProductStructureIdForLabor: 0,
    StoreId: storeId,
    ContactInfoId: contactInfoId,
    grpPricingContractId: "",
    IsContract: true,
  });
  try {
    const response = yield call(
      axios.get,
      `${API_URL}/User/api/CustomerInfo/GetLaborForPricingContract?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const rows = response.data?.result ?? response.data;
    // The catalog row's structure can describe the addon; keep the requested
    // primary structure for matching against the saved estimate section.
    return (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, productStructureId }));
  } catch (error) {
    if (error?.response?.status === 401) throw error;
    // An unavailable catalog must not prevent the saved estimate from opening.
    return [];
  }
}

export function* uploadCCScopePhoto({ token, file, scopeBlockId }) {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("scopeBlockId", String(scopeBlockId));
    const response = yield call(
      axios.post,
      `${API_URL}/Ordering/api/Order/UploadCCScopePhoto`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const photoUrl = response.data?.photoUrl || response.data || "";
    yield put({ type: CC_SCOPE_PHOTO_UPLOAD_SUCCESS, payload: { scopeBlockId, photoUrl } });
  } catch (error) {
    if (error?.response?.status === 401) {
      yield put({ type: SESSION_EXPIRED });
    } else {
      yield put({ type: CC_SCOPE_PHOTO_UPLOAD_FAILED, payload: error?.response });
    }
  }
}

export function* getCountertopCabinetScopes({ token, contactInfoId, unitTypeId }) {
  try {
    const response = yield call(
      axios.get,
      `${API_URL}/Ordering/api/Order/GetScopes?ContactInfoId=${contactInfoId}&UnitTypeId=${unitTypeId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    yield put({ type: CC_SCOPES_SUCCESS, payload: response.data });
  } catch (error) {
    if (error && error.response && error.response.status === 401) {
      yield put({ type: SESSION_EXPIRED });
    } else {
      yield put({ type: CC_SCOPES_FAILED, payload: error?.response });
    }
  }
}

export default function* rootCountertopCabinetEstimate() {
  yield all([
    takeLatest(CC_ESTIMATE_SAVE_REQUEST, saveCountertopCabinetEstimate),
    takeLatest(CC_ESTIMATE_LOAD_REQUEST, loadCountertopCabinetEstimate),
    takeLatest(CC_SCOPE_PHOTO_UPLOAD_REQUEST, uploadCCScopePhoto),
    takeLatest(CC_SCOPES_REQUEST, getCountertopCabinetScopes),
  ]);
}
